/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import React from "react";
import { useSelector } from "store";

import { StreamingActionHeader } from "./StreamingActionHeader";
import { StreamingAnnouncement } from "./StreamingAnnouncement";
import StreamingLiveCommerceV2 from "./StreamingLiveCommerceV2";
import { StreamingLiveEventV2 } from "./StreamingLiveEventV2";

import type { CommentFilterType } from "modules/streaming";
import type { FC } from "react";
import type { EventInfo } from "utility";

type Props = {
  eventInfo: EventInfo;
  mainVideoId: string;
  THEOPlayerElementHeight: number;
  isChatAdmin: boolean;
  isOpenAnnouncement: boolean;
  isEditingAnnouncement: boolean;
  selectedCommentFilter: CommentFilterType;
  streamingAnnouncement: string;
  handleToggleAnnouncement: () => void;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  setStreamingAnnouncement: (announcement: string) => void;
  onStreamingAnnouncementLocalDeleted: () => void;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
  handleCloseActionArea: () => void;
  isLiveEventEnabled: boolean;
  isOpenLiveEvent: boolean;
  handleToggleLiveEvent: () => void;
  setSelectedPaymentType: (type: "Card" | "CVS" | "Phone") => void;
  setShowPurchasingModal: (show: boolean) => void;
};

/**
 * UserActionArea
 * comment, gift, (later: goods sales, quiz, vote)
 * @param props
 */
const StreamingActionAreaV2Goods: React.FC<Props> = (props) => {
  // ----- Redux State -----
  const isDarkModeEnabled = useSelector(
    (state) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const streamingType = useSelector((state) => state.streaming.streamingType);

  return (
    <React.Fragment>
      <StreamingActionAreaLayout
        THEOPlayerElementHeight={props.THEOPlayerElementHeight}
        header={
          <StreamingActionHeader
            isDarkModeEnabled={isDarkModeEnabled}
            handleChangeCommentFilter={props.handleChangeCommentFilter}
            handleToggleAnnouncement={props.handleToggleAnnouncement}
            handleCloseActionArea={props.handleCloseActionArea}
            selectedCommentFilter={props.selectedCommentFilter}
            showCommentFilter={false}
            showAnnouncement={
              props.isChatAdmin || props.streamingAnnouncement !== ""
            }
            isLiveEventEnabled={props.isLiveEventEnabled}
            handleToggleLiveEvent={props.handleToggleLiveEvent}
          />
        }
      >
        {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

        <StreamingAnnouncement
          eventId={props.eventInfo.eventId}
          mainVideoId={props.mainVideoId}
          streamingType={streamingType}
          isOpenAnnouncement={props.isOpenAnnouncement}
          isEditingAnnouncement={props.isEditingAnnouncement}
          streamingAnnouncement={props.streamingAnnouncement}
          setIsEditingAnnouncement={props.setIsEditingAnnouncement}
          setStreamingAnnouncement={props.setStreamingAnnouncement}
          onStreamingAnnouncementLocalDeleted={
            props.onStreamingAnnouncementLocalDeleted
          }
        />

        <Box flex={"1 1 auto"}>
          <StreamingLiveCommerceV2
            mainVideoId={props.mainVideoId}
            THEOPlayerElementHeight={props.THEOPlayerElementHeight}
            setSelectedPaymentType={props.setSelectedPaymentType}
            setShowPurchasingModal={props.setShowPurchasingModal}
          />
        </Box>
      </StreamingActionAreaLayout>
    </React.Fragment>
  );
};
export default StreamingActionAreaV2Goods;

const StreamingActionAreaLayout: FC<{
  children: React.ReactNode;
  header: React.ReactNode;
  THEOPlayerElementHeight: number;
}> = ({ children, header, THEOPlayerElementHeight }) => {
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gridGap={"16px"}
      padding={"16px 16px 60px"}
      minHeight={`${THEOPlayerElementHeight}px`}
      css={css`
        height: calc(100vh - ${THEOPlayerElementHeight}px); /* fallback */
        @supports (height: 100dvh) {
          height: calc(100dvh - ${THEOPlayerElementHeight}px);
        }
      `}
    >
      {header}
      {children}
    </Box>
  );
};
